// src/components/issues/IssueRow.tsx — SCE Issues
// Rangée dense d'un problème — rendu bureau des listes (≥ 768px).
//
// Contrepartie bureau de ListeProblemesTelephone (rangées de 64 px, titre sur
// deux lignes entières), mêmes données, même destination au clic.
//
// ─── Ce qu'on gagne, ce qu'on perd ───
//
// Gagné : criticité et statut occupent une colonne FIXE, donc s'alignent
// verticalement d'une ligne à l'autre. Un coordinateur qui cherche ce qui est
// critique ou encore à qualifier balaie une colonne au lieu de lire six
// cartes. Mesuré sur six problèmes : 862px en cartes, 312px en rangées.
//
// Perdu : le titre est tronqué à une ligne. C'est le défaut assumé de cette
// mise en page, et la raison pour laquelle elle s'arrête à 768px.

import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { Badge } from '@/components/ui/Badge'
import { Cellule, RangeeDense } from '@/components/ui/ListeDense'
import { EtatProbleme } from '@/components/ui/EtatProbleme'
import { METIERS_COULEURS, METIERS_LABELS_COURT } from '@/config/metiers.config'
import { libelleBadge, type BadgeCloture, type Issue } from '@/types/issue.types'

/**
 * Gabarit de colonnes, partagé avec l'en-tête.
 *
 * Exporté pour que la page ne puisse pas déclarer un en-tête désaligné des
 * rangées — un décalage que ni le typecheck ni le build ne verraient.
 *
 * Largeurs calées sur la colonne de contenu de 728px : les colonnes fixes en
 * consomment 381, le titre prend les 347 restants.
 *
 * Lot 3 : colonne « Badge » (76px). Elle affiche le libellé
 * COURT — « Rapport de problème » ne tiendrait pas, il devient « Rapport » —
 * et le libellé complet reste en infobulle, la rangée dense étant réservée
 * au bureau, donc à la souris. Elle est vide pour tout problème non clos.
 *
 * 02/10 : criticité et statut fusionnés en une colonne « État » (icônes, libellés
 * au survol — components/ui/EtatProbleme), tout à droite comme dans le tableau
 * de « Tous les problèmes ».
 */
export const GABARIT_ISSUE = '3px 88px minmax(0, 1fr) 104px 76px 58px 52px'

export function IssueRow({ issue }: { issue: Issue }): JSX.Element {
  const ouvrirFiche = useOuvrirFiche()

  return (
    <RangeeDense
      gabarit={GABARIT_ISSUE}
      paddingLeft={0}
      onClick={() => ouvrirFiche(`/issues/${issue.id}`)}
    >
      {/* Liseré métier — la bordure gauche de la carte, ramenée à 3px.
          La couleur seule n'est pas une information accessible : le métier est
          donc aussi porté en texte, visible au survol et lu par un lecteur
          d'écran. Une colonne de 3px ne peut pas l'afficher autrement. */}
      <span
        className="lisere-metier"
        style={{ backgroundColor: METIERS_COULEURS[issue.metier] }}
        title={METIERS_LABELS_COURT[issue.metier]}
      >
        <span className="sr-only">{METIERS_LABELS_COURT[issue.metier]}</span>
      </span>

      <span style={{ paddingLeft: 11, minWidth: 0 }}>
        <Cellule chiffres secondaire>{issue.id}</Cellule>
      </span>

      <Cellule tronquee forte titreComplet={issue.titre}>
        {issue.titre}
      </Cellule>

      <Cellule chiffres secondaire tronquee>
        {issue.of || issue.pn || issue.numeroSerie}
      </Cellule>

      <Cellule>
        {issue.badgesCloture && <BadgesCompacts issue={issue} badges={issue.badgesCloture} />}
      </Cellule>

      <Cellule chiffres secondaire droite>
        {formatDateCourte(issue.dateDeclaration)}
      </Cellule>

      <Cellule>
        <EtatProbleme statut={issue.statut} criticite={issue.criticite} />
      </Cellule>
    </RangeeDense>
  )
}

// ─── En-tête ───

/**
 * Ligne de libellés de colonnes.
 *
 * La colonne du liseré métier ne porte pas de libellé : 3px ne titrent rien.
 * Le métier reste lisible au survol de chaque rangée.
 */
export function IssueRowHeader(): JSX.Element {
  return (
    <RangeeDense gabarit={GABARIT_ISSUE} paddingLeft={0} entete>
      <span aria-hidden="true" />
      <span style={{ paddingLeft: 11, minWidth: 0 }}>
        <Cellule entete>Réf.</Cellule>
      </span>
      <Cellule entete>Problème</Cellule>
      <Cellule entete>OF</Cellule>
      <Cellule entete>Badge</Cellule>
      <Cellule entete droite>Date</Cellule>
      <Cellule entete>État</Cellule>
    </RangeeDense>
  )
}

// ─── Badges de clôture en colonne étroite ───

/**
 * Plusieurs badges ne tiennent pas dans une colonne de 76px sur une rangée
 * d'une ligne. On montre le premier (ordre canonique), suivi de « +N » ; la
 * liste complète est dans l'infobulle — la rangée dense est réservée au
 * bureau, donc à la souris. La carte mobile, elle, les affiche tous.
 */
function BadgesCompacts({
  issue,
  badges,
}: {
  issue: Issue
  badges: readonly BadgeCloture[]
}): JSX.Element | null {
  const [premier, ...suivants] = badges
  if (!premier) return null
  return (
    <span
      className="inline-flex items-center gap-1"
      // L'infobulle donne les références complètes (« FNC-T_2026-55 ») :
      // la colonne de 76px, elle, n'a la place que du code court.
      title={badges.map((b) => libelleBadge(issue, b)).join(' · ')}
    >
      <Badge badgeCloture={premier} court />
      {suivants.length > 0 && (
        <span
          style={{
            fontSize: 'var(--font-size-tag)',
            fontWeight: 500,
            color: 'var(--color-text-secondary)',
          }}
        >
          +{suivants.length}
        </span>
      )}
    </span>
  )
}

// ─── Helpers ───

function formatDateCourte(iso: string): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    })
  } catch {
    return ''
  }
}
